import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CollaborationProvider } from "@/contexts/CollaborationContext";
import { Toaster } from "sonner";
import { Menu, Sparkles } from "lucide-react";
import AuthCallback from "@/components/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CompaniesPage from "@/pages/CompaniesPage";
import MediaPage from "@/pages/MediaPage";
import ContentCreatePage from "@/pages/ContentCreatePage";
import CalendarPage from "@/pages/CalendarPage";
import ApprovalPage from "@/pages/ApprovalPage";
import ProvidersPage from "@/pages/ProvidersPage";
import SocialAccountsPage from "@/pages/SocialAccountsPage";
import AdminConnectionsPage from "@/pages/AdminConnectionsPage";
import WeeklyPlannerPage from "@/pages/WeeklyPlannerPage";
import CampaignsPage from "@/pages/CampaignsPage";
import PromptTemplatesPage from "@/pages/PromptTemplatesPage";
import PerformancePage from "@/pages/PerformancePage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import OAuthCredentialsPage from "@/pages/OAuthCredentialsPage";
import LegalPage from "@/pages/LegalPage";
import BrandKitPage from "@/pages/BrandKitPage";
import ActivityLogPage from "@/pages/ActivityLogPage";
import TeamPage from "@/pages/TeamPage";
import ABTestPage from "@/pages/ABTestPage";
import CompetitorPage from "@/pages/CompetitorPage";
import WebhooksPage from "@/pages/WebhooksPage";
import MarketplacePage from "@/pages/MarketplacePage";
import PublicReviewPage from "@/pages/PublicReviewPage";
import TeamAnalyticsPage from "@/pages/TeamAnalyticsPage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import EmailSettingsPage from "@/pages/EmailSettingsPage";
import LlmKeysPage from "@/pages/LlmKeysPage";
import SocialInboxPage from "@/pages/SocialInboxPage";
import GroupDashboardPage from "@/pages/GroupDashboardPage";
import AdsHubPage from "@/pages/AdsHubPage";
import LeadsPage from "@/pages/LeadsPage";
import SetupPage from "@/pages/SetupPage";
import WebAnalyticsPage from "@/pages/WebAnalyticsPage";
import SeoPage from "@/pages/SeoPage";
import CockpitPage from "@/pages/CockpitPage";
import ZefixRadarPage from "@/pages/ZefixRadarPage";
import PublicLeadFormPage from "@/pages/PublicLeadFormPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import LandingPage from "@/pages/LandingPage";
import RegisterPage from "@/pages/RegisterPage";
import ImpressumPage from "@/pages/ImpressumPage";
import FeedbackWidget from "@/components/FeedbackWidget";
import Sidebar from "@/components/Sidebar";
import CollaborationBar from "@/components/CollaborationBar";

// "/" oeffentlich (Google-OAuth-Anforderung): anonym = Landing, eingeloggt = Dashboard
const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--studio-bg)]">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--studio-accent)] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return <ProtectedRoute><DashboardPage /></ProtectedRoute>;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--studio-bg)]" data-testid="loading-spinner">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--studio-accent)] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

      {/* Mobile Topbar */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white text-slate-900 flex items-center justify-between px-4 z-30 border-b border-slate-200"
        data-testid="mobile-topbar"
      >
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-btn"
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Menü öffnen"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/invictaflow-logo.png" alt="InvictaFlow" className="w-7 h-7 rounded-md" />
          <span className="font-heading text-sm font-bold tracking-tight">InvictaFlow</span>
        </div>
        <div className="w-9" />
      </header>

      <main
        className="flex-1 md:ml-64 p-4 pt-20 md:p-8 md:pt-8 animate-fade-in min-w-0"
        data-testid="main-content"
      >
        {children}
      </main>
      <CollaborationBar />
      <FeedbackWidget />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registrieren" element={<RegisterPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/anfrage/:companyId" element={<PublicLeadFormPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/datenschutz" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/nutzungsbedingungen" element={<TermsPage />} />
      <Route path="/review/:token" element={<PublicReviewPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/firmen" element={<ProtectedRoute><CompaniesPage /></ProtectedRoute>} />
      <Route path="/medien" element={<ProtectedRoute><MediaPage /></ProtectedRoute>} />
      <Route path="/content/erstellen" element={<ProtectedRoute><ContentCreatePage /></ProtectedRoute>} />
      <Route path="/wochenplaner" element={<ProtectedRoute><WeeklyPlannerPage /></ProtectedRoute>} />
      <Route path="/kampagnen" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/kalender" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/freigabe" element={<ProtectedRoute><ApprovalPage /></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/ads-manager" element={<ProtectedRoute><AdsHubPage /></ProtectedRoute>} />
      <Route path="/gruppe" element={<ProtectedRoute><GroupDashboardPage /></ProtectedRoute>} />
      <Route path="/google-ads" element={<ProtectedRoute><AdsHubPage /></ProtectedRoute>} />
      <Route path="/anzeigen" element={<ProtectedRoute><AdsHubPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
      <Route path="/web-analytics" element={<ProtectedRoute><WebAnalyticsPage /></ProtectedRoute>} />
      <Route path="/seo" element={<ProtectedRoute><SeoPage /></ProtectedRoute>} />
      <Route path="/cockpit" element={<ProtectedRoute><CockpitPage /></ProtectedRoute>} />
      <Route path="/firmen-radar" element={<ProtectedRoute><ZefixRadarPage /></ProtectedRoute>} />
      <Route path="/admin/oauth-credentials" element={<ProtectedRoute><OAuthCredentialsPage /></ProtectedRoute>} />
      <Route path="/admin/brand-kit" element={<ProtectedRoute><BrandKitPage /></ProtectedRoute>} />
      <Route path="/admin/activity-log" element={<ProtectedRoute><ActivityLogPage /></ProtectedRoute>} />
      <Route path="/content" element={<Navigate to="/freigabe" replace />} />
      <Route path="/ab-tests" element={<ProtectedRoute><ABTestPage /></ProtectedRoute>} />
      <Route path="/wettbewerber" element={<ProtectedRoute><CompetitorPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/provider" element={<ProtectedRoute><ProvidersPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><PromptTemplatesPage /></ProtectedRoute>} />
      <Route path="/social-media" element={<ProtectedRoute><SocialAccountsPage /></ProtectedRoute>} />
      <Route path="/admin/verbindungen" element={<ProtectedRoute><AdminConnectionsPage /></ProtectedRoute>} />
      <Route path="/webhooks" element={<ProtectedRoute><WebhooksPage /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
      <Route path="/team/analytics" element={<ProtectedRoute><TeamAnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedbackPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/admin/email" element={<ProtectedRoute><EmailSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/llm-keys" element={<ProtectedRoute><LlmKeysPage /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><SocialInboxPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <CollaborationProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
            </CollaborationProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
